import axios, { AxiosProgressEvent } from 'axios';

// Get values from environment variables
const CLOUDINARY_UPLOAD_PRESET = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET || 'portfolio_images';
const CLOUDINARY_CLOUD_NAME = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;
const CLOUDINARY_UPLOAD_URL = `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`;

/**
 * Validate Cloudinary configuration
 */
export const validateCloudinaryConfig = (): { isValid: boolean; missingVars: string[] } => {
    const missingVars: string[] = [];

    if (!process.env.REACT_APP_CLOUDINARY_CLOUD_NAME) {
        missingVars.push('REACT_APP_CLOUDINARY_CLOUD_NAME');
    }
    if (!process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET) {
        missingVars.push('REACT_APP_CLOUDINARY_UPLOAD_PRESET');
    }

    return {
        isValid: missingVars.length === 0,
        missingVars
    };
};

export interface CloudinaryUploadResponse {
    secure_url: string;
    public_id: string;
    format: string;
    width: number;
    height: number;
    resource_type: string;
}

/**
 * Upload image to Cloudinary using Axios
 */
export const uploadImageToCloudinary = async (
    file: File,
    progressCallback?: (progress: number) => void
): Promise<string> => {
    if (!CLOUDINARY_CLOUD_NAME) {
        throw new Error('Cloudinary cloud name is not defined in environment variables');
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);
    formData.append('folder', 'portfolio_projects');

    try {
        const response = await axios.post(CLOUDINARY_UPLOAD_URL, formData, {
            onUploadProgress: (progressEvent: AxiosProgressEvent) => {
                if (progressCallback && progressEvent.total) {
                    const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    progressCallback(progress);
                }
            },
        });

        return response.data.secure_url;
    } catch (error) {
        console.error('Error uploading to Cloudinary:', error);
        throw error;
    }
};

/**
 * Helper function to open Cloudinary's upload widget
 */
export const openCloudinaryUploadWidget = (
    options: {
        folder?: string;
        multiple?: boolean;
        maxFiles?: number;
        resourceType?: string;
        onSuccess: (urls: string[]) => void;
        onError?: (error: Error) => void;
    }
): void => {
    // @ts-ignore - Cloudinary widget is loaded globally
    if (typeof window.cloudinary === 'undefined') {
        console.error('Cloudinary widget not loaded');
        if (options.onError) {
            options.onError(new Error('Cloudinary widget not loaded'));
        }
        return;
    }

    // @ts-ignore - Cloudinary widget is loaded globally
    const widget = window.cloudinary.createUploadWidget(
        {
            cloudName: process.env.REACT_APP_CLOUDINARY_CLOUD_NAME,
            uploadPreset: process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET,
            folder: options.folder || 'portfolio_projects',
            multiple: options.multiple !== undefined ? options.multiple : true,
            maxFiles: options.maxFiles || 10,
            resourceType: options.resourceType || 'auto',
            sources: ['local', 'url', 'camera'],
            clientAllowedFormats: ['png', 'gif', 'jpg', 'jpeg', 'bmp', 'tiff', 'ico', 'pdf', 'ps', 'psd', 'webp', 'svg'],
            maxImageFileSize: 10000000, // 10MB
            maxVideoFileSize: 100000000, // 100MB for animated GIFs
            showAdvancedOptions: false,
            cropping: false,
            styles: {
                palette: {
                    window: '#FFFFFF',
                    windowBorder: '#90A0B3',
                    tabIcon: '#0078FF',
                    menuIcons: '#5A616A',
                    textDark: '#000000',
                    textLight: '#FFFFFF',
                    link: '#0078FF',
                    action: '#FF620C',
                    inactiveTabIcon: '#0E2F5A',
                    error: '#F44235',
                    inProgress: '#0078FF',
                    complete: '#20B832',
                    sourceBg: '#E4EBF1'
                },
                fonts: {
                    default: null,
                    "'Poppins', sans-serif": {
                        url: 'https://fonts.googleapis.com/css?family=Poppins',
                        active: true
                    }
                }
            }
        },
        (error: any, result: any) => {
            if (!error && result && result.event === 'success') {
                const url = result.info.secure_url;
                options.onSuccess([url]);
            } else if (error && options.onError) {
                options.onError(error);
            }
        }
    );

    widget.open();
};

/**
 * Upload base64 image to Cloudinary
 */
export const uploadBase64ImageToCloudinary = async (
    base64Image: string,
    folder: string = 'portfolio_projects'
): Promise<string> => {
    // Check for required environment variables
    if (!CLOUDINARY_CLOUD_NAME) {
        const missingVars = [];
        if (!process.env.REACT_APP_CLOUDINARY_CLOUD_NAME) missingVars.push('REACT_APP_CLOUDINARY_CLOUD_NAME');
        if (!process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET) missingVars.push('REACT_APP_CLOUDINARY_UPLOAD_PRESET');

        const errorMessage = `Cloudinary environment variables are missing: ${missingVars.join(', ')}. Please add these to your .env file.`;
        console.error(errorMessage);
        throw new Error(errorMessage);
    }

    // Validate base64 image format
    if (!base64Image || !base64Image.startsWith('data:')) {
        throw new Error('Invalid image format. Expected base64 data URL.');
    }

    // Check if the base64 string is just a placeholder or empty
    if (base64Image === 'data:,' || base64Image.length < 50) {
        throw new Error('Image data is empty or invalid.');
    }

    console.log(`Uploading image to Cloudinary folder: ${folder}`);
    console.log(`Cloud name: ${CLOUDINARY_CLOUD_NAME}`);
    console.log(`Upload preset: ${CLOUDINARY_UPLOAD_PRESET}`);
    console.log(`Image size: ${Math.round(base64Image.length / 1024)} KB`);

    try {
        const formData = new FormData();
        formData.append('file', base64Image);
        formData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);
        formData.append('folder', folder);

        const response = await axios.post(CLOUDINARY_UPLOAD_URL, formData, {
            timeout: 60000, // Increased to 60 seconds
            headers: {
                'Content-Type': 'multipart/form-data',
            },
            onUploadProgress: (progressEvent) => {
                if (progressEvent.total) {
                    const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    console.log(`Upload progress: ${progress}%`);
                }
            }
        });

        if (!response.data?.secure_url) {
            throw new Error('Upload succeeded but no URL returned from Cloudinary');
        }

        console.log('Successfully uploaded to Cloudinary:', response.data.secure_url);
        return response.data.secure_url;
    } catch (error: any) {
        console.error('Error uploading base64 image to Cloudinary:', error);

        if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
            throw new Error('Upload timed out. Please try with a smaller image or check your internet connection.');
        } else if (error.response?.status === 400) {
            throw new Error('Invalid upload request. Please check the image format and try again.');
        } else if (error.response?.status === 401) {
            throw new Error('Cloudinary authentication failed. Please check your upload preset configuration.');
        } else if (error.response?.status >= 500) {
            throw new Error('Cloudinary server error. Please try again later.');
        } else if (error.message?.includes('Network Error')) {
            throw new Error('Network error. Please check your internet connection and try again.');
        } else if (error.message?.includes('Image data is empty')) {
            throw new Error('No image selected. Please select an image to upload.');
        }

        throw new Error(`Image upload failed: ${error.message || 'Unknown error'}`);
    }
}; 